import React, { useEffect, useState } from 'react';
import { api } from '../lib/axios';
import { useAuth } from '../context/AuthContext';

/**
 * MarqueeBanner Component
 * Optimized for mobile with delicate font scaling and smooth scrolling.
 * Displays regional or global default announcements to Pet Owners.
 */
const DEFAULT_MESSAGE = "✨ We are onboarding your nearest pet service providers! Thank you for your patience. Till then, feel free to explore the application. ✨";

export const MarqueeBanner: React.FC = () => {
    const { user } = useAuth();
    const [config, setConfig] = useState<{ enabled: boolean; message: string } | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await api.get('/owners/me');
                const partner = res.data.partner;
                
                const message = partner?.marqueeMessage || DEFAULT_MESSAGE;
                const isEnabled = partner ? (partner.marqueeEnabled ?? true) : true;
                
                setConfig({ enabled: isEnabled, message });
            } catch (error) {
                console.error('Failed to fetch marquee config', error);
                setConfig({ enabled: true, message: DEFAULT_MESSAGE });
            } finally {
                setLoading(false);
            }
        };

        if (user?.role === 'OWNER') {
            fetchConfig();
        }
    }, [user]);

    if (loading || !config || !config.enabled) {
        return null;
    }

    const handleClose = () => {
        setConfig({ ...config, enabled: false });
    };

    return (
        <div className="relative ios-safe-marquee bg-gradient-to-r from-indigo-700 via-purple-700 to-pink-700 text-white shadow-md z-[200] border-b border-white/10 px-4 py-2 flex items-center justify-between">
            <div className="flex-1 text-center truncate">
                <span className="text-[11px] md:text-sm font-bold tracking-wider">
                    {config.message}
                </span>
            </div>
            <button 
                onClick={handleClose}
                className="ml-4 p-1 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
                aria-label="Close"
            >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
};
